import { useMemo } from 'react';
import styles from './ScenarioGenerator.module.css';
import { useSeedancePresetActors } from '../../../features/seedance-preset-actors';
import type {
  SeedancePresetActorItem,
} from '../state/useGenerationState';

type Props = {
  // 複数AI俳優対応: 選択中 preset の id 集合と選択順 (登場人物A/B…)。
  selectedActorIds: Set<string>;
  orderById: Map<string, number>;
  // 複数選択時は A/B バッジを出す。
  isMultiEnabled: boolean;
  // カードクリック (トグル) / 「指定しない」(全クリア)。
  onToggleActor: (actor: SeedancePresetActorItem) => void;
  onClear: () => void;
  // フィルタ state は SetupPanel と共有。性別/年代の UI は CastingSection 側に
  // 一本化したので、ここではプリセット絞り込みのために読み取るだけ (setter は不要)。
  genderFilter: string;
  ageFilter: number | null;
  // AI俳優「入れ替え」モード (ScenarioActorSwapModal §3.4) 用。既に配列内にある
  // (置換対象の位置を除く) 俳優を選択不可にする。未指定時は誰も無効化しない。
  disabledActorIds?: Set<string>;
  // 置換モードでは「指定しない」を出さない (選ぶと人数が減ってしまい置換にならない)。
  hideNoneOption?: boolean;
};

export function SeedancePresetActorPicker({
  selectedActorIds, orderById, isMultiEnabled,
  onToggleActor, onClear,
  genderFilter, ageFilter,
  disabledActorIds, hideNoneOption,
}: Props) {
  const filters = useMemo(() => {
    const f: { gender?: 'male' | 'female' | 'neutral'; age?: number } = {};
    if (genderFilter === 'male' || genderFilter === 'female' || genderFilter === 'neutral') {
      f.gender = genderFilter;
    }
    if (ageFilter !== null) f.age = ageFilter;
    return f;
  }, [genderFilter, ageFilter]);

  const query = useSeedancePresetActors(filters);

  const isNothingSelected = selectedActorIds.size === 0;

  return (
    <>
      {/* preset カード一覧 */}
      <div className={styles.seedancePresetHeading}>
        <span className={styles.seedancePresetHeadingIcon} aria-hidden="true" />
        <span>Seedance専用AI俳優</span>
      </div>
      <div className={styles.castingCarousel}>
        <div className={`${styles.castingScrollContainer} ${styles.castingScrollMagnify}`}>
          <ul className={styles.castingGrid}>
            {!hideNoneOption && (
              <li>
                <button
                  type="button"
                  className={`${styles.castingCard} ${styles.castingCardNone} ${styles.seedanceNoneCard} ${isNothingSelected ? styles.castingCardSelected : ''}`}
                  onClick={onClear}
                  aria-pressed={isNothingSelected}
                >
                  <div className={styles.castingImageNone}>
                    <img src="/anonyface.png" alt="" className={styles.castingNoneIcon} style={{ opacity: 0.5 }} />
                  </div>
                  <div className={styles.castingMeta}>指定しない</div>
                </button>
              </li>
            )}
            {query.isLoading ? (
              <li className={styles.castingLoading}>プリセットを取得中…</li>
            ) : query.isError ? (
              <li className={styles.castingError}>プリセットの取得に失敗しました</li>
            ) : query.data?.actors?.map((actor) => {
              const isSelected = selectedActorIds.has(actor.id);
              const orderIdx = orderById.get(actor.id);
              const personLetter = (isMultiEnabled && isSelected && orderIdx !== undefined)
                ? String.fromCharCode(65 + orderIdx)
                : null;
              const isDisabled = disabledActorIds?.has(actor.id) ?? false;
              return (
                <li key={actor.id}>
                  <button
                    type="button"
                    className={`${styles.castingCard} ${styles.seedanceActorCard} ${styles.castingCardActor} ${isSelected ? styles.castingCardSelected : ''}`}
                    onClick={() => {
                      if (isDisabled) return;
                      onToggleActor({
                        id: actor.id,
                        byteplus_asset_id: actor.byteplus_asset_id,
                        display_name: actor.display_name,
                        thumbnail_public_url: actor.thumbnail_public_url ?? undefined,
                        gender: actor.gender ?? undefined,
                        min_age: actor.min_age ?? undefined,
                        max_age: actor.max_age ?? undefined,
                        ethnicity: actor.ethnicity ?? undefined,
                      });
                    }}
                    disabled={isDisabled}
                    title={isDisabled ? '既に他の登場人物に割り当てられています' : undefined}
                    style={isDisabled ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
                    aria-pressed={isSelected}
                  >
                    {personLetter && (
                      <span className={styles.castingExclusiveBadge} style={{ left: 4, right: 'auto', background: 'rgba(79, 70, 229, 0.9)' }}>
                        {`登場人物${personLetter}`}
                      </span>
                    )}
                    <div className={`${styles.castingImage} ${styles.castingImageFull}`}>
                      {actor.thumbnail_public_url ? (
                        <img src={actor.thumbnail_public_url} alt={actor.display_name} />
                      ) : (
                        <div className={styles.castingImagePlaceholder} />
                      )}
                      <div className={styles.castingNameOverlay}>{actor.display_name}</div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
}
